import { useEffect, useRef } from "react";
import { Helmet } from 'react-helmet-async';
import { Link, useLocation } from "react-router-dom";
import { getCanonicalUrl, getDefaultOgImage } from '@/lib/seoUtils';
import { Shield, Lock, Eye, Database, UserCheck, Globe, Mail, ArrowRight, Sparkles, AlertCircle, RefreshCw } from "lucide-react";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";

/* ── Scroll reveal ── */
function useReveal<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { el.classList.add("visible"); observer.unobserve(el); } },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return ref;
}

const sections = [
  { id: "information-we-collect", label: "Information We Collect" },
  { id: "how-we-use", label: "How We Use Your Information" },
  { id: "sharing", label: "Sharing & Disclosure" },
  { id: "cookies", label: "Cookies & Tracking" },
  { id: "data-retention", label: "Data Retention" },
  { id: "your-rights", label: "Your Rights" },
  { id: "security", label: "Security" },
  { id: "children", label: "Children's Privacy" },
  { id: "international", label: "International Transfers" },
  { id: "changes", label: "Policy Changes" },
  { id: "contact", label: "Contact Us" },
];

const highlights = [
  { icon: Shield, title: "We don't sell your data", desc: "Your personal information is never sold to third parties for advertising or any other purpose." },
  { icon: Lock, title: "Encrypted in transit & at rest", desc: "All data is protected using industry-standard TLS encryption and AES-256 at rest." },
  { icon: Eye, title: "Minimal data collection", desc: "We only collect what's necessary to provide the service. Nothing more." },
  { icon: UserCheck, title: "You stay in control", desc: "Access, correct, export, or delete your data at any time from your account settings." },
];

export default function PrivacyPage() {
  const heroRef = useReveal<HTMLDivElement>();
  const { pathname } = useLocation();

  const title = 'Privacy Policy | BookMyCoaching';
  const description = "Read BookMyCoaching's privacy policy.";
  const canonicalUrl = getCanonicalUrl(pathname);
  const ogImage = getDefaultOgImage();

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={ogImage} />
      </Helmet>
      <Header />

      <main>
        {/* ── Hero ── */}
        <section className="relative pt-28 pb-16 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-[hsl(230,55%,15%)] via-[hsl(210,55%,18%)] to-[hsl(190,45%,14%)]" />
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <div className="absolute -top-32 -right-32 w-[500px] h-[500px] bg-secondary/10 rounded-full blur-[150px]" />
            <div className="absolute -bottom-32 -left-32 w-[400px] h-[400px] bg-[hsl(260,60%,50%)]/8 rounded-full blur-[120px]" />
          </div>

          <div ref={heroRef} className="container mx-auto px-4 relative z-10 text-center reveal">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-secondary mb-5 px-3 py-1 rounded-full bg-secondary/10 border border-secondary/20">
              <Sparkles className="w-3 h-3" />
              Legal
            </span>
            <h1 className="font-sans text-4xl sm:text-5xl text-white mb-5 leading-tight">
              Privacy Policy
            </h1>
            <p className="text-white/60 max-w-xl mx-auto leading-relaxed mb-4">
              We take your privacy seriously. This policy explains what data we collect, why we collect it, and how you can control it.
            </p>
            <p className="text-white/35 text-sm">
              Last updated: <span className="text-white/55 font-medium">1 April 2025</span>
              &nbsp;·&nbsp; Effective: <span className="text-white/55 font-medium">1 April 2025</span>
            </p>
          </div>
        </section>

        {/* ── Highlights ── */}
        <section className="py-14 bg-muted/30 border-b border-border/40">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 max-w-6xl mx-auto">
              {highlights.map((h, i) => (
                <div key={i} className="flex gap-4 items-start p-5 rounded-2xl bg-card border border-border/40">
                  <div className="w-10 h-10 rounded-xl bg-secondary/10 flex items-center justify-center flex-shrink-0">
                    <h.icon className="w-5 h-5 text-secondary" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground mb-1">{h.title}</p>
                    <p className="text-xs text-muted-foreground leading-relaxed">{h.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Main content ── */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto flex gap-12">

              {/* Sticky sidebar nav */}
              <aside className="hidden lg:block w-56 flex-shrink-0">
                <div className="sticky top-24">
                  <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-4">Contents</p>
                  <nav className="flex flex-col gap-1">
                    {sections.map((s) => (
                      <a
                        key={s.id}
                        href={`#${s.id}`}
                        className="text-sm text-muted-foreground hover:text-secondary transition-colors py-1 pl-3 border-l-2 border-transparent hover:border-secondary"
                      >
                        {s.label}
                      </a>
                    ))}
                  </nav>
                </div>
              </aside>

              {/* Body */}
              <article className="flex-1 min-w-0 prose-custom">

                {/* Intro */}
                <div className="mb-10 p-6 rounded-2xl bg-secondary/5 border border-secondary/20 flex gap-4">
                  <AlertCircle className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-foreground mb-1">Plain-language summary</p>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      BookMyCoaching ("we", "us", "our") operates the BookMyCoaching platform accessible at <span className="font-medium text-foreground">bookmycoaching.com</span>. This Privacy Policy explains how we handle personal data. By using our platform, you agree to this policy. If you disagree, please discontinue use.
                    </p>
                  </div>
                </div>

                {/* 1. Information We Collect */}
                <div id="information-we-collect" className="mb-12 scroll-mt-24">
                  <SectionHeading icon={Database} number="1" title="Information We Collect" />
                  <p className="text-muted-foreground mb-4 leading-relaxed">We collect information in three ways: information you give us, information we collect automatically, and information from third parties.</p>

                  <SubHeading>1.1 Information You Provide</SubHeading>
                  <Table rows={[
                    ["Account registration", "Name, email address, password, profile photo, phone number (optional)"],
                    ["Teacher profile", "Qualifications, teaching experience, subject expertise, availability, bank account details for payouts"],
                    ["Student profile", "Learning goals, grade level, preferred subjects, availability"],
                    ["Inquiries & messages", "Content of messages exchanged between students and teachers on the platform"],
                    ["Payments", "Billing address; payment card details are processed directly by our payment processor and never stored on our servers"],
                    ["Support requests", "Communications you send to our support team"],
                  ]} />

                  <SubHeading>1.2 Information Collected Automatically</SubHeading>
                  <Table rows={[
                    ["Usage data", "Pages visited, features used, session duration, clicks, search queries"],
                    ["Device & browser", "IP address, browser type and version, operating system, device identifiers"],
                    ["Log data", "Server logs including timestamps, referrer URLs, and error reports"],
                    ["Location", "Approximate location derived from IP address (country/city level only; no precise GPS)"],
                  ]} />

                  <SubHeading>1.3 Information from Third Parties</SubHeading>
                  <ul className="list-disc pl-5 text-muted-foreground text-sm space-y-2 leading-relaxed">
                    <li><strong className="text-foreground">Authentication providers</strong> — if you sign in with Google or Apple, we receive your name and email as shared by those services.</li>
                    <li><strong className="text-foreground">Payment processors</strong> — Stripe shares transaction status and partial card metadata (last 4 digits, card brand) to help us display billing history.</li>
                    <li><strong className="text-foreground">Background check partners</strong> — for teacher verification, we share and receive identity data with our screening partner under a separate data processing agreement.</li>
                  </ul>
                </div>

                {/* 2. How We Use */}
                <div id="how-we-use" className="mb-12 scroll-mt-24">
                  <SectionHeading icon={Eye} number="2" title="How We Use Your Information" />
                  <p className="text-muted-foreground mb-4 leading-relaxed">We process personal data only for the following purposes:</p>
                  <Table rows={[
                    ["Service delivery", "Creating and managing accounts, facilitating teaching sessions, processing payments, and providing customer support"],
                    ["AI matching", "Analysing learning preferences and teacher expertise to surface the most relevant recommendations"],
                    ["Platform safety", "Detecting fraud, verifying identities, investigating abuse reports, and enforcing our Terms of Service"],
                    ["Communications", "Sending transactional emails (booking confirmations, reminders, invoices) and, where consented, product updates"],
                    ["Analytics & improvement", "Understanding how users interact with the platform to improve features, fix bugs, and prioritise roadmap work"],
                    ["Legal compliance", "Meeting obligations under applicable law, responding to lawful requests from authorities, and resolving disputes"],
                  ]} />
                  <p className="text-muted-foreground text-sm mt-4 leading-relaxed">
                    <strong className="text-foreground">Legal basis (GDPR):</strong> We rely on <em>contract performance</em> for delivering the service, <em>legitimate interests</em> for safety and analytics, <em>consent</em> for marketing emails, and <em>legal obligation</em> for compliance purposes.
                  </p>
                </div>

                {/* 3. Sharing */}
                <div id="sharing" className="mb-12 scroll-mt-24">
                  <SectionHeading icon={Globe} number="3" title="Sharing & Disclosure" />
                  <p className="text-muted-foreground mb-4 leading-relaxed">We do not sell, rent, or trade your personal data. We share it only in the following limited circumstances:</p>
                  <ul className="list-disc pl-5 text-muted-foreground text-sm space-y-3 leading-relaxed">
                    <li><strong className="text-foreground">Between students and teachers</strong> — when a student sends an inquiry, the teacher receives the student's name, message, and contact details. Students see the teacher's public profile.</li>
                    <li><strong className="text-foreground">Service providers</strong> — we engage third-party vendors (cloud hosting, payment processing, email delivery, analytics) under strict data processing agreements. They process data only as instructed.</li>
                    <li><strong className="text-foreground">Business transfers</strong> — if BookMyCoaching is acquired or merges with another entity, your data may be transferred as part of that transaction. We will notify you before this occurs.</li>
                    <li><strong className="text-foreground">Legal requirements</strong> — we may disclose data if required by law, court order, or to protect the rights, property, or safety of BookMyCoaching, our users, or the public.</li>
                    <li><strong className="text-foreground">Aggregated/anonymous data</strong> — we may share non-identifiable, aggregated statistics (e.g. "India is our fastest-growing market") publicly or with partners.</li>
                  </ul>
                </div>

                {/* 4. Cookies */}
                <div id="cookies" className="mb-12 scroll-mt-24">
                  <SectionHeading icon={Lock} number="4" title="Cookies & Tracking Technologies" />
                  <p className="text-muted-foreground mb-4 leading-relaxed">We use cookies and similar technologies to keep you signed in, remember your preferences, and understand how the platform is used.</p>
                  <Table rows={[
                    ["Essential cookies", "Required for core functionality — authentication sessions, CSRF protection, and load balancing. Cannot be disabled."],
                    ["Preference cookies", "Remember your language, theme, and display settings across sessions."],
                    ["Analytics cookies", "Collect anonymised usage data to help us improve the product (e.g. PostHog, Google Analytics). You can opt out in cookie settings."],
                    ["Marketing cookies", "Used only if you have opted in to marketing communications. Allow us to measure campaign effectiveness."],
                  ]} />
                  <p className="text-muted-foreground text-sm mt-4">
                    You can manage cookie preferences at any time via the <span className="text-secondary font-medium cursor-pointer">Cookie Settings</span> link in the footer. Note that disabling essential cookies will prevent you from using the platform.
                  </p>
                </div>

                {/* 5. Data Retention */}
                <div id="data-retention" className="mb-12 scroll-mt-24">
                  <SectionHeading icon={Database} number="5" title="Data Retention" />
                  <p className="text-muted-foreground mb-4 leading-relaxed">We keep personal data only for as long as necessary for the purposes described in this policy or as required by law.</p>
                  <Table rows={[
                    ["Active account data", "Retained for the lifetime of the account plus 30 days after deletion request"],
                    ["Message history", "Retained for 2 years after the last message, then automatically purged"],
                    ["Payment records", "Retained for 7 years to comply with Indian GST and financial regulations"],
                    ["Server logs", "Retained for 90 days, then deleted"],
                    ["Backup copies", "Encrypted backups may persist for up to 60 days after deletion before being overwritten"],
                  ]} />
                </div>

                {/* 6. Your Rights */}
                <div id="your-rights" className="mb-12 scroll-mt-24">
                  <SectionHeading icon={UserCheck} number="6" title="Your Rights" />
                  <p className="text-muted-foreground mb-4 leading-relaxed">Depending on your location, you may have the following rights over your personal data:</p>
                  <ul className="list-disc pl-5 text-muted-foreground text-sm space-y-3 leading-relaxed">
                    <li><strong className="text-foreground">Access</strong> — request a copy of all personal data we hold about you.</li>
                    <li><strong className="text-foreground">Correction</strong> — ask us to correct inaccurate or incomplete data.</li>
                    <li><strong className="text-foreground">Deletion</strong> — request erasure of your data ("right to be forgotten"), subject to legal retention obligations.</li>
                    <li><strong className="text-foreground">Portability</strong> — receive your data in a structured, machine-readable format (JSON or CSV).</li>
                    <li><strong className="text-foreground">Restriction</strong> — ask us to restrict processing while a dispute is being resolved.</li>
                    <li><strong className="text-foreground">Objection</strong> — object to processing based on legitimate interests or for direct marketing.</li>
                    <li><strong className="text-foreground">Withdraw consent</strong> — where processing is consent-based, you may withdraw at any time without affecting prior processing.</li>
                  </ul>
                  <p className="text-muted-foreground text-sm mt-4 p-4 rounded-xl bg-muted/50 border border-border/40">
                    To exercise any of these rights, email us at{" "}
                    <a href="mailto:support@bookmycoaching.com" className="text-secondary hover:underline underline-offset-4">support@bookmycoaching.com</a>{" "}
                    or use the data controls in your Account Settings. We will respond within 30 days.
                  </p>
                </div>

                {/* 7. Security */}
                <div id="security" className="mb-12 scroll-mt-24">
                  <SectionHeading icon={Shield} number="7" title="Security" />
                  <p className="text-muted-foreground mb-4 leading-relaxed">We implement industry-standard technical and organisational measures to protect your data, including:</p>
                  <ul className="list-disc pl-5 text-muted-foreground text-sm space-y-2 leading-relaxed">
                    <li>TLS 1.2+ encryption for all data in transit</li>
                    <li>AES-256 encryption for sensitive data at rest</li>
                    <li>Password hashing using bcrypt with a high work factor</li>
                    <li>Role-based access controls limiting employee access to personal data on a need-to-know basis</li>
                    <li>Regular third-party penetration testing and automated vulnerability scanning</li>
                    <li>Multi-factor authentication available (and required) for all staff accounts</li>
                  </ul>
                  <p className="text-muted-foreground text-sm mt-4 leading-relaxed">
                    No system is impenetrable. In the event of a data breach that poses a risk to your rights, we will notify affected users and relevant regulators within 72 hours as required by applicable law.
                  </p>
                </div>

                {/* 8. Children */}
                <div id="children" className="mb-12 scroll-mt-24">
                  <SectionHeading icon={UserCheck} number="8" title="Children's Privacy" />
                  <p className="text-muted-foreground leading-relaxed">
                    BookMyCoaching is not directed at children under 13 years of age. We do not knowingly collect personal data from children under 13. If you are a parent or guardian and believe your child has provided us with personal data, please contact us at{" "}
                    <a href="mailto:support@bookmycoaching.com" className="text-secondary hover:underline underline-offset-4">support@bookmycoaching.com</a>{" "}
                    and we will delete the data promptly.
                  </p>
                  <p className="text-muted-foreground leading-relaxed mt-3">
                    Users aged 13–18 may use the platform only with verifiable parental or guardian consent.
                  </p>
                </div>

                {/* 9. International */}
                <div id="international" className="mb-12 scroll-mt-24">
                  <SectionHeading icon={Globe} number="9" title="International Data Transfers" />
                  <p className="text-muted-foreground leading-relaxed">
                    BookMyCoaching is headquartered in India. If you are accessing our platform from the European Economic Area (EEA), United Kingdom, or other regions with data protection laws, please be aware that your information may be transferred to and processed in India and other countries.
                  </p>
                  <p className="text-muted-foreground leading-relaxed mt-3">
                    For transfers from the EEA/UK, we rely on Standard Contractual Clauses (SCCs) approved by the European Commission. For transfers to other countries, we ensure equivalent protection through contractual safeguards with our service providers.
                  </p>
                </div>

                {/* 10. Changes */}
                <div id="changes" className="mb-12 scroll-mt-24">
                  <SectionHeading icon={RefreshCw} number="10" title="Policy Changes" />
                  <p className="text-muted-foreground leading-relaxed">
                    We may update this Privacy Policy from time to time. When we make material changes, we will notify you by:
                  </p>
                  <ul className="list-disc pl-5 text-muted-foreground text-sm space-y-2 leading-relaxed mt-3">
                    <li>Posting the updated policy with a new "Last updated" date at the top of this page</li>
                    <li>Sending an email notification to your registered address at least 14 days before changes take effect</li>
                    <li>Displaying a banner on the platform on your next login</li>
                  </ul>
                  <p className="text-muted-foreground text-sm mt-4">
                    Your continued use of the platform after the effective date constitutes acceptance of the updated policy. If you disagree with any changes, you may delete your account before the effective date.
                  </p>
                </div>

                {/* 11. Contact */}
                <div id="contact" className="mb-4 scroll-mt-24">
                  <SectionHeading icon={Mail} number="11" title="Contact Us" />
                  <p className="text-muted-foreground mb-6 leading-relaxed">
                    If you have questions, concerns, or requests regarding this Privacy Policy or your personal data, please reach out:
                  </p>
                  <div className="rounded-2xl border border-border/40 bg-card p-6 grid sm:grid-cols-2 gap-6">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">Privacy Team</p>
                      <p className="text-sm font-semibold text-foreground mb-1">BookMyCoaching Pvt. Ltd.</p>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        Urbtech Trade Centre<br />
                        Sector 132, Noida 201304<br />
                        India
                      </p>
                    </div>
                    <div className="flex flex-col gap-3">
                      <a href="mailto:support@bookmycoaching.com" className="inline-flex items-center gap-2 text-sm text-secondary hover:underline underline-offset-4">
                        <Mail className="w-4 h-4" />
                        support@bookmycoaching.com
                      </a>
                      <Link to="/contact" className="inline-flex items-center gap-2 text-sm text-secondary hover:underline underline-offset-4">
                        <ArrowRight className="w-4 h-4" />
                        Use our contact form
                      </Link>
                    </div>
                  </div>
                  <p className="text-muted-foreground text-sm mt-4">
                    We aim to respond to all privacy-related enquiries within <strong className="text-foreground">5 business days</strong>.
                    For data subject requests, the legal maximum response time is 30 days.
                  </p>
                </div>
              </article>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

/* ── Sub-components ── */
function SectionHeading({ icon: Icon, number, title }: { icon: React.ElementType; number: string; title: string }) {
  return (
    <div className="flex items-center gap-3 mb-5">
      <div className="w-9 h-9 rounded-xl bg-secondary/10 flex items-center justify-center flex-shrink-0">
        <Icon className="w-4 h-4 text-secondary" />
      </div>
      <h2 className="font-sans text-xl sm:text-2xl text-foreground">
        <span className="text-muted-foreground font-normal text-base mr-2">{number}.</span>
        {title}
      </h2>
    </div>
  );
}

function SubHeading({ children }: { children: React.ReactNode }) {
  return <h3 className="font-semibold text-foreground text-sm uppercase tracking-wide mt-6 mb-3">{children}</h3>;
}

function Table({ rows }: { rows: [string, string][] }) {
  return (
    <div className="rounded-xl border border-border/40 overflow-hidden mb-4">
      <table className="w-full text-sm">
        <tbody>
          {rows.map(([label, value], i) => (
            <tr key={i} className={i % 2 === 0 ? "bg-muted/30" : "bg-background"}>
              <td className="px-4 py-3 font-medium text-foreground w-1/3 align-top border-r border-border/40">{label}</td>
              <td className="px-4 py-3 text-muted-foreground leading-relaxed">{value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
